import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { UpgradeModalProvider } from "@/contexts/UpgradeModalContext";
import Index from "./pages/Index";

// Code-split all non-landing routes
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const UserPage = lazy(() => import("./pages/UserPage"));
const DownloadProduct = lazy(() => import("./pages/DownloadProduct"));
const LinkInBioCreator = lazy(() => import("./pages/seo/LinkInBioCreator"));
const LinkInBioInstagram = lazy(() => import("./pages/seo/LinkInBioInstagram"));
const MonetizzareInstagram = lazy(() => import("./pages/seo/MonetizzareInstagram"));
const VendereProdottiDigitali = lazy(() => import("./pages/seo/VendereProdottiDigitali"));
const LinkInBioGratis = lazy(() => import("./pages/seo/LinkInBioGratis"));
const PrenotazioniOnline = lazy(() => import("./pages/seo/PrenotazioniOnline"));
const ProvaGratuita = lazy(() => import("./pages/ProvaGratuita"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ChiSiamo = lazy(() => import("./pages/ChiSiamo"));
const Contatti = lazy(() => import("./pages/Contatti"));
const TerminiUso = lazy(() => import("./pages/TerminiUso"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const Subscription = lazy(() => import("./pages/Subscription"));
const ResumeCheckout = lazy(() => import("./pages/ResumeCheckout"));
const ActivateTrial = lazy(() => import("./pages/ActivateTrial"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <UpgradeModalProvider>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/download" element={<DownloadProduct />} />
              <Route path="/chi-siamo" element={<ChiSiamo />} />
              <Route path="/contatti" element={<Contatti />} />
              <Route path="/termini" element={<TerminiUso />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/cookie" element={<CookiePolicy />} />
              <Route path="/booking-success" element={<BookingSuccess />} />
              <Route path="/abbonamento" element={<Subscription />} />
              <Route path="/riprendi" element={<ResumeCheckout />} />
              <Route path="/attiva-trial" element={<ActivateTrial />} />
              <Route path="/link-in-bio-creator" element={<LinkInBioCreator />} />
              <Route path="/link-in-bio-instagram" element={<LinkInBioInstagram />} />
              <Route path="/monetizzare-instagram" element={<MonetizzareInstagram />} />
              <Route path="/vendere-prodotti-digitali-instagram" element={<VendereProdottiDigitali />} />
              <Route path="/link-in-bio-gratis" element={<LinkInBioGratis />} />
              <Route path="/prenotazioni-online" element={<PrenotazioniOnline />} />
              <Route path="/prova-gratuita" element={<ProvaGratuita />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/:username" element={<UserPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </UpgradeModalProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
